import { VERSION_LANGS, LANG_NAMES, type CaptionLang } from "@hudhud/core";
import { DownloadsMenu } from "../components/DownloadsMenu.js";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type ClipWithFiles, type LectureSummary } from "../api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";

const PLATFORMS = ["youtube", "instagram", "facebook", "tiktok", "x"] as const;
const LABEL: Record<string, string> = { youtube: "YouTube", instagram: "Instagram", facebook: "Facebook", tiktok: "TikTok", x: "X" };

type Item = { clip: ClipWithFiles; lecture: LectureSummary };

export const Posting: React.FC = () => {
  const [items, setItems] = useState<Item[] | null>(null);
  const toast = useToast();
  const load = async () => {
    const { lectures } = await api.lectures();
    const all = await Promise.all(lectures.filter((l) => !l.archived).map((l) => api.lecture(l.videoId).then((d) => d.clips.filter((c) => c.status !== "rejected" && (!!c.files.render || ["approved", "rendered", "posted"].includes(c.status))).map((clip) => ({ clip, lecture: l })))));
    setItems(all.flat().sort((a, b) => (a.clip.plan.postAt ?? "9").localeCompare(b.clip.plan.postAt ?? "9")));
  };
  useEffect(() => { load().catch((e) => toast(e.message)); const t = setInterval(() => load().catch(() => {}), 6000); return () => clearInterval(t); }, []);
  const now = new Date().toISOString();
  const list = items ?? [];
  const groups = {
    due: list.filter((i) => i.clip.status !== "posted" && (!i.clip.plan.postAt || i.clip.plan.postAt <= now)),
    scheduled: list.filter((i) => i.clip.status !== "posted" && i.clip.plan.postAt && i.clip.plan.postAt > now),
    posted: list.filter((i) => i.clip.status === "posted"),
  };
  return (
    <div className="main">
      <div className="page-head"><div><h1>Posting</h1><div className="sub">{items ? `${groups.due.length} ready to post · ${groups.scheduled.length} scheduled · ${groups.posted.length} posted · Melbourne time` : "Loading…"}</div></div></div>
      <div className="page col" style={{ gap: 22 }}>
        <Group title="Ready to post" note="Rendered. Upload by hand, copy the caption, then tick each platform." items={groups.due} onChange={load} />
        <Group title="Scheduled" note="Waiting for their planned time." items={groups.scheduled} onChange={load} />
        <Group title="Posted" note="Ticked on every platform." items={groups.posted} onChange={load} />
      </div>
    </div>
  );
};

const Group: React.FC<{ title: string; note: string; items: Item[]; onChange: () => Promise<void> }> = ({ title, note, items, onChange }) => (
  <section className="col" style={{ gap: 10 }}>
    <div className="row" style={{ alignItems: "baseline" }}><span style={{ fontSize: 15, fontWeight: 800 }}>{title}</span><span className="sub">{note}</span></div>
    {items.length ? items.map((i) => <PostCard key={`${i.clip.videoId}-${i.clip.id}`} item={i} onChange={onChange} />) : <div className="muted" style={{ fontSize: 13, padding: "2px 0" }}>Nothing here.</div>}
  </section>
);

export const PostCard: React.FC<{ item: Item; onChange: () => Promise<void> }> = ({ item: { clip, lecture }, onChange }) => {
  const toast = useToast();
  const [lang, setLang] = useState<CaptionLang>("en");
  const copy = async (platform: string) => {
    const m = (lang === "en" || lang === "ar") && clip.meta[platform]?.[lang] ? clip.meta[platform]?.[lang] : lang !== "en" ? clip.versions[lang]?.meta[platform] : undefined;
    if (!m) return toast("No caption yet");
    const text = platform === "x" ? m.title : `${m.title}\n\n${m.description}${m.description.includes("#") ? "" : `\n\n${m.hashtags.map((h) => `#${h}`).join(" ")}`}`;
    await navigator.clipboard.writeText(text);
    toast(`${LABEL[platform]} caption copied (${lang.toUpperCase()})`);
  };
  const mark = (platform: string, undo: boolean) => api.action(clip.videoId, clip.id, "markPosted", { platform, at: undo ? null : undefined }).then(onChange).catch((e) => toast(e.message));
  const plan = (v: string) => api.action(clip.videoId, clip.id, "plan", { postAt: v ? new Date(v).toISOString() : null }).then(onChange).catch((e) => toast(e.message));
  const local = clip.plan.postAt ? new Date(clip.plan.postAt) : null;
  const inputVal = local ? new Date(local.getTime() - local.getTimezoneOffset() * 60000).toISOString().slice(0, 16) : "";
  const langs = ["en", "ar", ...VERSION_LANGS.filter((l) => l !== "ar" && clip.versions[l])] as CaptionLang[];
  return (
    <div className="clipcard" style={{ gap: 14, padding: 14 }}>
      <Link to={`/clips/${clip.videoId}/${clip.id}`} className="pc" style={{ width: 92 }} title="Open the clip">{clip.files.thumb ? <img src={clip.files.thumb} alt="" /> : null}</Link>
      <div className="body" style={{ gap: 10 }}>
        <div className="row" style={{ alignItems: "flex-start" }}>
          <div className="col" style={{ gap: 3, minWidth: 0 }}><span style={{ fontSize: 15, fontWeight: 800 }}>{clip.hook.text}</span><span className="sub" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{lecture.title} · {lecture.speaker}</span></div>
          <label className="row chip solid" style={{ marginLeft: "auto", padding: "4px 10px", fontSize: 12, flexShrink: 0 }} title="When you plan to post it"><Icon name="clock" size={13} color="var(--muted)" /><input type="datetime-local" value={inputVal} onChange={(e) => plan(e.target.value)} style={{ background: "transparent", border: 0, padding: 0, color: "var(--text)", fontSize: 12 }} /></label>
        </div>
        <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
          <span className="hint" style={{ marginRight: 4 }}>Posted on</span>
          {PLATFORMS.map((p) => { const at = clip.plan.posted?.[p]; return <button key={p} className={`chip ${at ? "green" : ""}`} onClick={() => mark(p, !!at)} title={at ? `Posted ${new Date(at).toLocaleString()} · click to undo` : "Click when posted"}>{at ? <Icon name="check" size={12} /> : null}{LABEL[p]}</button>; })}
        </div>
        <div className="row" style={{ gap: 6, marginTop: "auto", flexWrap: "wrap" }}>
          <span className="hint" style={{ marginRight: 4 }}>Copy caption</span>
          {langs.length > 1 ? <div className="seg" title="Caption language">{langs.map((l) => <button key={l} className={l === lang ? "on" : ""} onClick={() => setLang(l)}>{l === "en" ? "EN" : LANG_NAMES[l]?.slice(0, 2).toUpperCase() ?? l.toUpperCase()}</button>)}</div> : null}
          {PLATFORMS.map((p) => <button key={p} className="btn sm" onClick={() => copy(p)}><Icon name="copy" size={13} />{LABEL[p]}</button>)}
          <span className="spacer" />
          {clip.files.pack ? null : <span className="chip gold">Rendering…</span>}
          {clip.files.render ? <a className="btn sm quiet" href={clip.files.render} target="_blank" rel="noreferrer"><Icon name="eye" size={14} />Watch</a> : null}
          <DownloadsMenu videoId={clip.videoId} clipId={clip.id} files={clip.files} />
        </div>
      </div>
    </div>
  );
};
